import {Stack} from "@mui/material";
import type {MediaLink, MediaType} from "../../../../../sdo/MediaLink.ts";
import {MEDIA_TYPE_OPTIONS} from "../../../../../sdo/MediaLink.ts";
import {F, OperatorToggle, SummaryPill, SummaryPillRow} from "../resolvers/editors/ResolverUtils.tsx";

interface MediaEditorProps {
    draft: MediaLink;
    editing: boolean;
    onChange: (updated: MediaLink) => void;
}

// Local row style: full width, left-aligned content (rather than centered)
const mediaRowSx = {
    width: "100%",
    alignItems: "center",
    justifyContent: "flex-start",
    flexWrap: "wrap" as const,
    gap: "8px",
    padding: "3px 0px",
};

function formatDateTime(epochMs: number): string {
    if (!epochMs || epochMs <= 0) return "";
    const d = new Date(epochMs);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function formatCoordinates(lat: number, lon: number): string {
    if (!lat && !lon) return "";
    return `${lat}, ${lon}`;
}

function MediaEditor({draft, editing, onChange}: MediaEditorProps) {
    const upd = <K extends keyof MediaLink>(key: K, value: MediaLink[K]) => {
        onChange({...draft, [key]: value});
    };

    const updNum = (key: "latitude" | "longitude", raw: string) => {
        const n = parseFloat(raw);
        upd(key, isNaN(n) ? 0 : n);
    };

    const hasText = (s: string | undefined | null) => !!(s && s.trim().length > 0);

    if (!editing) {
        return (
            <Stack direction="column" spacing={0.5} sx={{width: "100%", maxWidth: 760, mx: "auto", alignItems: "stretch"}}>
                <SummaryPillRow>
                    <SummaryPill label="Type" value={draft.mediaType || "—"}/>
                    <SummaryPill label="Date / Time" value={formatDateTime(draft.datetime) || "—"}/>
                </SummaryPillRow>
                <SummaryPillRow>
                    <SummaryPill label="Source URL" value={hasText(draft.sourceURL) ? draft.sourceURL : "—"}/>
                </SummaryPillRow>
                {hasText(draft.caption) && (
                    <SummaryPillRow>
                        <SummaryPill label="Caption" value={draft.caption}/>
                    </SummaryPillRow>
                )}
                {(hasText(draft.location) || draft.latitude !== 0 || draft.longitude !== 0) && (
                    <SummaryPillRow>
                        {hasText(draft.location) && <SummaryPill label="Location" value={draft.location}/>}
                        {(draft.latitude !== 0 || draft.longitude !== 0) && (
                            <SummaryPill label="Coordinates" value={formatCoordinates(draft.latitude, draft.longitude)}/>
                        )}
                    </SummaryPillRow>
                )}
                {hasText(draft.details) && (
                    <SummaryPillRow>
                        <SummaryPill label="Details" value={draft.details}/>
                    </SummaryPillRow>
                )}
            </Stack>
        );
    }

    return (
        <Stack direction="column" spacing={1} sx={{width: "100%", maxWidth: 760, mx: "auto", alignItems: "stretch"}}>
            <Stack direction="row" sx={mediaRowSx}>
                <OperatorToggle
                    value={draft.mediaType}
                    options={MEDIA_TYPE_OPTIONS.map((o) => ({label: o.label, value: o.value}))}
                    onChange={(v) => upd("mediaType", v as MediaType)}
                />
            </Stack>
            <Stack direction="row" sx={mediaRowSx}>
                <F value={draft.sourceURL} placeholder="Source URL" onChange={(v) => upd("sourceURL", v)} flex="1 1 auto"/>
            </Stack>
            <Stack direction="row" sx={mediaRowSx}>
                <F value={draft.caption} placeholder="Caption" onChange={(v) => upd("caption", v)} flex="1 1 auto"/>
            </Stack>
            <Stack direction="row" sx={mediaRowSx}>
                <F value={draft.location} placeholder="Location" onChange={(v) => upd("location", v)} flex="1 1 auto"/>
                <F value={draft.latitude || ""} placeholder="Latitude" onChange={(v) => updNum("latitude", v)} width="140px"/>
                <F value={draft.longitude || ""} placeholder="Longitude" onChange={(v) => updNum("longitude", v)} width="140px"/>
            </Stack>
            <Stack direction="row" sx={mediaRowSx}>
                <F value={draft.details} placeholder="Additional Details" onChange={(v) => upd("details", v)} flex="1 1 auto"/>
            </Stack>
        </Stack>
    );
}

export default MediaEditor;
